import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, MapPin, PhoneCall, Truck, XCircle } from 'lucide-react';
import { supabase } from '../supabase';

/**
 * Page ouverte par le client depuis le SMS reçu à la création de sa commande.
 *
 * Il confirme, demande un rappel ou annule, sans attendre l'appel d'un
 * closer : sur 60 jours, une commande confirmée dans l'heure est annulée deux
 * fois moins souvent qu'une commande appelée 4 à 24 h plus tard.
 *
 * Tout passe par trois fonctions serveur qui ne reconnaissent que le jeton
 * secret de la commande — jamais son numéro, qui se devine.
 */

const NAVY = '#173b78';
const GREEN = '#16a34a';
const BRICK = '#c53a2d';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100dvh', background: '#f0f3f8', fontFamily: 'Alan Sans, system-ui, sans-serif' }}>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      {/* Le logo est blanc : il lui faut le bandeau navy de l'app */}
      <div style={{ background: 'linear-gradient(90deg, #07162d 0%, #112c52 100%)', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src="/logo-wakaana.png" alt="Wakaana Express" style={{ height: '58px', objectFit: 'contain' }} />
      </div>
      <div style={{ maxWidth: '440px', margin: '0 auto', padding: '22px 18px 40px', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: 'white', borderRadius: '20px', border: '1px solid #eef0f6', boxShadow: '0 8px 30px rgba(19,40,72,0.08)', padding: '24px 20px', ...style }}>
      {children}
    </div>
  );
}

function Message({ icon: Icon, color, title, text, children }) {
  return (
    <Card style={{ textAlign: 'center' }}>
      <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <Icon size={30} color={color} />
      </div>
      <div className="ui-sheet-title" style={{ fontSize: '19px', marginBottom: '8px' }}>{title}</div>
      {text && <div style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>{text}</div>}
      {children}
    </Card>
  );
}

const bigButton = (background, color = 'white', border = 'none') => ({
  width: '100%', padding: '16px', borderRadius: '14px', border, background, color,
  fontSize: '16px', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
});

export default function ConfirmOrder() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [askCancel, setAskCancel] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState('idle'); // idle | asking | shared | refused

  const load = useCallback(async () => {
    const { data, error: rpcError } = await supabase.rpc('client_order_view', { p_token: token });
    if (rpcError) setError("La commande n'a pas pu être chargée. Vérifiez votre connexion.");
    setOrder(data?.[0] || null);
    if (data?.[0]?.has_position) setPosition('shared');
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (action) => {
    setBusy(true);
    setError('');
    const { data, error: rpcError } = await supabase.rpc('client_order_respond', { p_token: token, p_action: action });
    setBusy(false);
    setAskCancel(false);
    if (rpcError || data === 'introuvable' || data === 'action_invalide') {
      setError("Votre réponse n'a pas pu être enregistrée. Réessayez dans un instant.");
      return;
    }
    await load();
  };

  const sharePosition = () => {
    if (!navigator.geolocation) { setPosition('refused'); return; }
    setPosition('asking');
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { data } = await supabase.rpc('client_order_share_position', {
          p_token: token, p_lat: coords.latitude, p_lng: coords.longitude, p_accuracy: coords.accuracy ?? null,
        });
        setPosition(data ? 'shared' : 'refused');
      },
      () => setPosition('refused'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  if (loading) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: '60px' }}>
          <Loader2 size={26} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </Shell>
    );
  }

  if (!order) {
    return (
      <Shell>
        <Message icon={AlertTriangle} color="#c2410c" title="Lien introuvable"
          text={error || "Ce lien n'est pas valide. Vérifiez qu'il est complet, ou contactez la boutique."} />
      </Shell>
    );
  }

  // Proposé après confirmation : aide le livreur, et c'est le bon moment pour le demander.
  const positionBlock = order.status !== 'delivered' && order.status !== 'failed' && (
    <div style={{ marginTop: '20px', paddingTop: '18px', borderTop: '1px solid #f1f5f9' }}>
      {position === 'shared' ? (
        <div style={{ fontSize: '13px', color: GREEN, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <CheckCircle2 size={15} /> Position transmise au livreur
        </div>
      ) : (
        <>
          <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6, marginBottom: '12px' }}>
            Partagez votre position : le livreur vous trouvera plus vite.
          </div>
          <button onClick={sharePosition} disabled={position === 'asking'} style={bigButton('white', NAVY, `1.5px solid ${NAVY}`)}>
            {position === 'asking'
              ? <><Loader2 size={17} style={{ animation: 'spin 1s linear infinite' }} /> Localisation…</>
              : <><MapPin size={17} /> Partager ma position</>}
          </button>
          {position === 'refused' && (
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '10px', lineHeight: 1.5 }}>
              La position n'a pas pu être obtenue. Autorisez la localisation dans votre navigateur, ou indiquez un repère au livreur.
            </div>
          )}
        </>
      )}
    </div>
  );

  // ── Commande déjà traitée : on dit où elle en est, sans rien proposer ──
  if (order.status === 'delivered') {
    return <Shell><Message icon={CheckCircle2} color={GREEN} title="Commande livrée" text="Merci de votre confiance." /></Shell>;
  }
  if (order.confirmation_status === 'cancelled' || order.status === 'failed') {
    return <Shell><Message icon={XCircle} color={BRICK} title="Commande annulée" text="Si c'est une erreur, contactez directement la boutique." /></Shell>;
  }
  if (order.status === 'transit') {
    return (
      <Shell>
        <Message icon={Truck} color={NAVY} title="Votre commande est en route" text="Le livreur vous contactera à son arrivée.">
          {positionBlock}
        </Message>
      </Shell>
    );
  }
  if (order.confirmation_status === 'confirmed') {
    return (
      <Shell>
        <Message icon={CheckCircle2} color={GREEN} title="Commande confirmée"
          text={`Merci ${order.client_first_name || ''} ! Nous préparons la livraison de votre commande chez ${order.store || 'la boutique'}.`}>
          {positionBlock}
        </Message>
      </Shell>
    );
  }
  if (order.client_response === 'recall') {
    return (
      <Shell>
        <Message icon={PhoneCall} color={NAVY} title="C'est noté"
          text="Un conseiller va vous rappeler rapidement pour finaliser votre commande." />
      </Shell>
    );
  }

  // ── En attente de la réponse du client ──
  return (
    <Shell>
      <div style={{ fontSize: '15px', color: '#475569', marginBottom: '14px', lineHeight: 1.5 }}>
        Bonjour <strong style={{ color: '#0f172a' }}>{order.client_first_name || ''}</strong>, voici votre commande chez <strong style={{ color: '#0f172a' }}>{order.store}</strong> :
      </div>

      <Card style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
          {order.product_name || 'Votre commande'}{order.quantity > 1 ? ` × ${order.quantity}` : ''}
        </div>
        {order.amount != null && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '12px' }}>
            <span style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '28px', fontWeight: 800, color: NAVY, lineHeight: 1 }}>{fmt(order.amount)}</span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8' }}>FCFA, à payer à la livraison</span>
          </div>
        )}
        {order.address && (
          <div style={{ display: 'flex', gap: '6px', fontSize: '13px', color: '#64748b', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
            <MapPin size={14} style={{ flexShrink: 0, marginTop: '2px' }} /> {order.address}
          </div>
        )}
      </Card>

      {error && (
        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c', borderRadius: '12px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {askCancel ? (
        <Card style={{ textAlign: 'center' }}>
          <div className="ui-sheet-title" style={{ marginBottom: '6px' }}>Annuler la commande ?</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Elle ne vous sera pas livrée.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button onClick={() => respond('cancel')} disabled={busy} style={bigButton(BRICK)}>
              {busy ? <Loader2 size={17} style={{ animation: 'spin 1s linear infinite' }} /> : 'Oui, annuler'}
            </button>
            <button onClick={() => setAskCancel(false)} disabled={busy} style={bigButton('white', '#475569', '1.5px solid #e2e8f0')}>
              Non, je garde ma commande
            </button>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button onClick={() => respond('confirm')} disabled={busy}
            style={{ ...bigButton(GREEN), boxShadow: '0 6px 18px rgba(22,163,74,0.28)' }}>
            {busy ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <><CheckCircle2 size={18} /> Je confirme ma commande</>}
          </button>
          <button onClick={() => respond('recall')} disabled={busy} style={bigButton('white', NAVY, `1.5px solid #bfdbfe`)}>
            <PhoneCall size={17} /> Rappelez-moi
          </button>
          <button onClick={() => setAskCancel(true)} disabled={busy}
            style={{ ...bigButton('transparent', '#94a3b8'), fontSize: '14px', fontWeight: 600, padding: '12px' }}>
            Je souhaite annuler
          </button>
        </div>
      )}
    </Shell>
  );
}
